import { useEffect, useState } from "react";
import { MessageSquare, ShieldCheck, ShieldOff, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LABELS = {
  gesendet: { text: "Gesendet", cls: "bg-green-500/20 text-green-500" },
  gesendet_test: { text: "Gesendet (Test)", cls: "bg-blue-500/20 text-blue-400" },
  ausstehend: { text: "Ausstehend", cls: "bg-yellow-500/20 text-yellow-500" },
  fehlgeschlagen: { text: "Fehlgeschlagen", cls: "bg-red-500/20 text-red-500" },
  geplant: { text: "Geplant", cls: "bg-purple-500/20 text-purple-300" },
  entfaellt: { text: "Entfällt", cls: "bg-white/10 text-[#A1A1AA]" },
};

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " Uhr" : "–";

export const SmsBadge = ({ value, testId }) => {
  const cfg = LABELS[value] || LABELS.entfaellt;
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] whitespace-nowrap ${cfg.cls}`} data-testid={testId}>{cfg.text}</span>;
};

export const SmsCell = ({ apt }) => {
  const sms = apt.sms || {};
  const id = apt.appointment_id;
  return (
    <div className="space-y-1 text-xs" data-testid={`sms-cell-${id}`}>
      <div className={sms.consent ? "text-green-500" : "text-[#71717A]"} data-testid={`sms-consent-${id}`}>
        Einwilligung: {sms.consent ? "Ja" : "Nein"}
      </div>
      {sms.consent && (
        <>
          <div className="flex items-center gap-1">Best.: <SmsBadge value={sms.confirmation} testId={`sms-confirmation-${id}`} /></div>
          <div className="flex items-center gap-1">Erinn.: <SmsBadge value={sms.reminder?.status} testId={`sms-reminder-${id}`} /></div>
        </>
      )}
    </div>
  );
};

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-2 py-1">
    <span className="text-[#71717A]">{label}</span>
    <span className="text-right">{children}</span>
  </div>
);

export const SmsDetailPanel = ({ apt, adminToken, onChanged }) => {
  const sms = apt.sms || {};
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/appointments/admin/sms/log?appointment_id=${apt.appointment_id}`, { headers: { "X-Admin-Token": adminToken } })
      .then((r) => r.json()).then((d) => setLogs(d.logs || [])).catch(() => setLogs([]));
  }, [apt.appointment_id, adminToken]);

  const revoke = async () => {
    if (!window.confirm("SMS-Einwilligung für diesen Termin widerrufen?")) return;
    const res = await fetch(`${API_URL}/api/appointments/admin/${apt.appointment_id}/sms-revoke`, { method: "POST", headers: { "X-Admin-Token": adminToken } });
    if (res.ok) { toast.success("SMS-Einwilligung widerrufen"); onChanged?.(); } else toast.error("Fehler beim Widerruf");
  };

  return (
    <div className="bg-white/5 rounded-lg p-3 text-sm" data-testid="sms-detail-panel">
      <p className="font-semibold flex items-center gap-2 mb-2">
        <MessageSquare className="w-4 h-4 text-[#c1ff72]" /> SMS-Benachrichtigungen
        {sms.mode === "test" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400" data-testid="sms-test-mode-badge">TESTMODUS – kein echter Versand</span>}
      </p>
      <Row label="Einwilligung">
        <span className={`inline-flex items-center gap-1 ${sms.consent ? "text-green-500" : "text-[#A1A1AA]"}`} data-testid="sms-detail-consent">
          {sms.consent ? <ShieldCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
          {sms.consent ? `Ja (seit ${fmtDateTime(sms.consent_at)})` : sms.revoked_at ? `Widerrufen (${fmtDateTime(sms.revoked_at)})` : "Nein"}
        </span>
      </Row>
      {sms.mobile_masked && <Row label="Mobilnummer"><span className="font-mono">{sms.mobile_masked}</span></Row>}
      <Row label="Anfrage-SMS"><SmsBadge value={sms.request_received || "entfaellt"} testId="sms-detail-request" /></Row>
      <Row label="Bestätigungs-SMS"><SmsBadge value={sms.confirmation} testId="sms-detail-confirmation" /></Row>
      {sms.rejection && <Row label="Absage-SMS"><SmsBadge value={sms.rejection} testId="sms-detail-rejection" /></Row>}
      <Row label="Erinnerungs-SMS"><SmsBadge value={sms.reminder?.status} testId="sms-detail-reminder" /></Row>
      <Row label="Geplanter Erinnerungszeitpunkt">
        <span className="inline-flex items-center gap-1" data-testid="sms-detail-reminder-at"><Clock className="w-3 h-3" />{fmtDateTime(sms.reminder?.due_at)}</span>
      </Row>
      {sms.reminder?.reason && <p className="text-xs text-[#71717A] mt-1" data-testid="sms-detail-reminder-reason">Hinweis: {sms.reminder.reason}</p>}

      {logs.length > 0 && (
        <div className="mt-3 border-t border-white/10 pt-2 space-y-2" data-testid="sms-log-list">
          <p className="text-xs text-[#71717A]">Protokoll</p>
          {logs.map((l) => (
            <div key={l.log_id} className="text-xs bg-[#0A0A0A] rounded p-2">
              <div className="flex justify-between"><span>{l.kind} · {l.mode}</span><span>{l.status} · {fmtDateTime(l.updated_at)}</span></div>
              <p className="text-[#A1A1AA] mt-1">{l.body}</p>
              {l.error && <p className="text-red-400 mt-1">{l.error}</p>}
            </div>
          ))}
        </div>
      )}

      {sms.consent && (
        <Button variant="outline" size="sm" onClick={revoke} className="mt-3 border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="btn-sms-revoke-admin">
          SMS-Einwilligung widerrufen
        </Button>
      )}
    </div>
  );
};
